/**
 * Debug utility for development and debugging
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface DebugOptions {
    prefix?: string;
    timestamp?: boolean;
    level?: LogLevel;
}

class Debugger {
    private isDevelopment: boolean;
    private isDebugEnabled: boolean;

    constructor() {
        this.isDevelopment = process.env.NODE_ENV === 'development';
        this.isDebugEnabled =
            this.isDevelopment ||
            process.env.NEXT_PUBLIC_DEBUG === 'true' ||
            (typeof window !== 'undefined' && (window as any).__DEBUG__ === true);
    }

    private formatMessage(level: LogLevel, message: string, options?: DebugOptions): string {
        const prefix = options?.prefix || 'DEBUG';
        const timestamp = options?.timestamp !== false
            ? new Date().toISOString()
            : '';

        return `[${prefix}]${timestamp ? ` [${timestamp}]` : ''} ${message}`;
    }

    private shouldLog(level: LogLevel): boolean {
        if (!this.isDebugEnabled) return false;

        // In production, only log errors and warnings unless explicitly enabled
        if (!this.isDevelopment && level !== 'error' && level !== 'warn') {
            return this.isDebugEnabled;
        }

        return true;
    }

    log(message: string, ...args: unknown[]): void {
        if (!this.shouldLog('log')) return;
        console.log(this.formatMessage('log', message), ...args);
    }

    info(message: string, ...args: unknown[]): void {
        if (!this.shouldLog('info')) return;
        console.info(this.formatMessage('info', message), ...args);
    }

    warn(message: string, ...args: unknown[]): void {
        if (!this.shouldLog('warn')) return;
        console.warn(this.formatMessage('warn', message), ...args);
    }

    error(message: string, error?: Error | unknown, ...args: unknown[]): void {
        if (!this.shouldLog('error')) return;
        const errorObj = error instanceof Error ? error : new Error(String(error));
        console.error(
            this.formatMessage('error', message),
            errorObj,
            ...args
        );
    }

    debug(message: string, ...args: unknown[]): void {
        if (!this.shouldLog('debug')) return;
        console.debug(this.formatMessage('debug', message), ...args);
    }

    group(label: string): void {
        if (!this.isDebugEnabled) return;
        console.group(label);
    }

    groupEnd(): void {
        if (!this.isDebugEnabled) return;
        console.groupEnd();
    }

    table(data: unknown): void {
        if (!this.isDebugEnabled) return;
        console.table(data);
    }

    time(label: string): void {
        if (!this.isDebugEnabled) return;
        console.time(label);
    }

    timeEnd(label: string): void {
        if (!this.isDebugEnabled) return;
        console.timeEnd(label);
    }

    trace(message?: string): void {
        if (!this.isDebugEnabled) return;
        if (message) {
            console.trace(message);
        } else {
            console.trace();
        }
    }

    /**
     * Log API request/response for debugging
     */
    api(method: string, url: string, data?: unknown, response?: unknown): void {
        if (!this.isDebugEnabled) return;
        this.group(`🌐 API ${method.toUpperCase()} ${url}`);
        if (data) {
            this.debug('Request:', data);
        }
        if (response) {
            this.debug('Response:', response);
        }
        this.groupEnd();
    }

    /**
     * Log component lifecycle events
     */
    component(name: string, event: 'mount' | 'unmount' | 'update', props?: unknown): void {
        if (!this.isDebugEnabled) return;
        this.debug(`⚛️  Component ${name} ${event}`, props ? { props } : '');
    }

    /**
     * Log state changes
     */
    state(name: string, oldState: unknown, newState: unknown): void {
        if (!this.isDebugEnabled) return;
        this.group(`🔄 State Change: ${name}`);
        this.debug('Previous:', oldState);
        this.debug('Current:', newState);
        this.groupEnd();
    }

    /**
     * Enable/disable debugging at runtime
     */
    enable(): void {
        this.isDebugEnabled = true;
        if (typeof window !== 'undefined') {
            (window as any).__DEBUG__ = true;
        }
        this.log('Debug mode enabled');
    }

    disable(): void {
        this.isDebugEnabled = false;
        if (typeof window !== 'undefined') {
            (window as any).__DEBUG__ = false;
        }
    }

    /**
     * Check if debugging is enabled
     */
    isEnabled(): boolean {
        return this.isDebugEnabled;
    }
}

// Export singleton instance
export const debug = new Debugger();

// Export class for custom instances
export { Debugger };

// Make debug available globally in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    (window as any).debug = debug;
}

